import { HttpClient, HttpEvent } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { API_BASE_URL } from '../../core/config/api-config';
import { Contact } from '../contacts/contacts.models';
import {
  ConversationDetail,
  ConversationDraftSummary,
  ConversationNote,
  ConversationRating,
  ConversationSearchPage,
  InboxPage,
  MergedConversationThread,
  MessagesPage,
  RatingStatistics,
  SendMessageResult,
} from './conversations.models';

/**
 * HTTP client for FiveWORX.Communication.API.Controllers.ConversationsController
 * (Sprint 2B Section 10). The JWT is attached automatically by jwt.interceptor; every
 * endpoint is already organization-scoped server-side from that token, so no organization
 * id is ever passed here.
 */
@Injectable({ providedIn: 'root' })
export class ConversationsApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL);

  private get conversationsUrl(): string {
    return `${this.baseUrl}/v1/conversations`;
  }

  getInbox(
    includeArchived = false,
    page = 1,
    pageSize = 25,
    queueId?: string,
    departmentId?: string,
    tag?: string,
  ): Observable<InboxPage> {
    const params: Record<string, string | number | boolean> = { includeArchived, page, pageSize };

    if (queueId) {
      params['queueId'] = queueId;
    }

    if (departmentId) {
      params['departmentId'] = departmentId;
    }

    if (tag) {
      params['tag'] = tag;
    }

    return this.http.get<InboxPage>(this.conversationsUrl, { params });
  }

  getConversation(conversationId: string): Observable<ConversationDetail> {
    return this.http.get<ConversationDetail>(`${this.conversationsUrl}/${conversationId}`);
  }

  /** Communication Platform v2's Full Conversation Search -- see
   * SearchConversationsQuery's own remarks on what it searches. */
  search(searchTerm: string, page = 1, pageSize = 5): Observable<ConversationSearchPage> {
    return this.http.get<ConversationSearchPage>(`${this.conversationsUrl}/search`, { params: { q: searchTerm, page, pageSize } });
  }

  /** Get-or-create: resolves this conversation's Contact, lazily creating and linking one
   * server-side if it was never set (e.g. a conversation that already existed before Contact
   * linking was introduced). */
  getOrCreateContact(conversationId: string): Observable<Contact> {
    return this.http.post<Contact>(`${this.conversationsUrl}/${conversationId}/contact`, {});
  }

  getMessages(conversationId: string, page = 1, pageSize = 50): Observable<MessagesPage> {
    return this.http.get<MessagesPage>(`${this.conversationsUrl}/${conversationId}/messages`, {
      params: { page, pageSize },
    });
  }

  /** Agent Workspace phase: the merged, separator-ready thread across every sibling
   * Conversation record for this customer -- see MergedConversationThread's own remarks.
   * Used by the thread page instead of getMessages(); getMessages() itself is left
   * unchanged since ticket message-selection still needs messages scoped to one real
   * conversationId. */
  getMergedThread(conversationId: string): Observable<MergedConversationThread> {
    return this.http.get<MergedConversationThread>(`${this.conversationsUrl}/${conversationId}/merged-thread`);
  }

  markRead(conversationId: string): Observable<void> {
    return this.http.post<void>(`${this.conversationsUrl}/${conversationId}/read`, {});
  }

  takeOver(conversationId: string, newOwnerUserId: string): Observable<void> {
    return this.http.post<void>(`${this.conversationsUrl}/${conversationId}/take-over`, { newOwnerUserId });
  }

  archive(conversationId: string): Observable<void> {
    return this.http.post<void>(`${this.conversationsUrl}/${conversationId}/archive`, {});
  }

  reopen(conversationId: string): Observable<void> {
    return this.http.post<void>(`${this.conversationsUrl}/${conversationId}/reopen`, {});
  }

  close(conversationId: string, reason?: string): Observable<void> {
    return this.http.post<void>(`${this.conversationsUrl}/${conversationId}/close`, reason ? { reason } : {});
  }

  transfer(conversationId: string, fromUserId: string | null, toUserId: string, reason?: string): Observable<void> {
    return this.http.post<void>(`${this.conversationsUrl}/${conversationId}/transfer`, { fromUserId, toUserId, reason });
  }

  /** Communication Platform v2: Conversation Queue Assignment -- independent of any
   * Ticket's own queue. queueId null clears the assignment. */
  assignToQueue(conversationId: string, queueId: string | null): Observable<void> {
    return this.http.post<void>(`${this.conversationsUrl}/${conversationId}/queue`, { queueId });
  }

  /** See assignToQueue's own remarks -- the Department equivalent. */
  assignToDepartment(conversationId: string, departmentId: string | null): Observable<void> {
    return this.http.post<void>(`${this.conversationsUrl}/${conversationId}/department`, { departmentId });
  }

  /** Communication Platform v2's Conversation Tags. */
  addTag(conversationId: string, tag: string): Observable<void> {
    return this.http.post<void>(`${this.conversationsUrl}/${conversationId}/tags`, { tag });
  }

  removeTag(conversationId: string, tag: string): Observable<void> {
    return this.http.delete<void>(`${this.conversationsUrl}/${conversationId}/tags/${encodeURIComponent(tag)}`);
  }

  /** Communication Platform v2's Internal Conversation Notes. */
  getNotes(conversationId: string): Observable<ConversationNote[]> {
    return this.http.get<ConversationNote[]>(`${this.conversationsUrl}/${conversationId}/notes`);
  }

  addNote(conversationId: string, content: string): Observable<{ success: boolean; noteId: string | null; failureReason: string | null }> {
    return this.http.post<{ success: boolean; noteId: string | null; failureReason: string | null }>(
      `${this.conversationsUrl}/${conversationId}/notes`,
      { content },
    );
  }

  /** Communication Platform v2's Persistent Draft Messages -- server-side, so a draft
   * survives a browser refresh and is visible from any of the agent's own tabs/devices. */
  getDraft(conversationId: string): Observable<{ text: string | null }> {
    return this.http.get<{ text: string | null }>(`${this.conversationsUrl}/${conversationId}/draft`);
  }

  saveDraft(conversationId: string, text: string): Observable<void> {
    return this.http.put<void>(`${this.conversationsUrl}/${conversationId}/draft`, { text });
  }

  /** Every draft the calling agent currently has, across every Conversation -- backs the
   * Inbox's own "draft" indicator. */
  getDrafts(): Observable<ConversationDraftSummary[]> {
    return this.http.get<ConversationDraftSummary[]>(`${this.conversationsUrl}/drafts`);
  }

  /** Communication Platform v2's Customer Rating -- the agent-facing view of whatever the
   * customer submitted (submission itself happens on the anonymous Web Chat surface). */
  getRating(conversationId: string): Observable<ConversationRating | null> {
    return this.http.get<ConversationRating | null>(`${this.conversationsUrl}/${conversationId}/rating`);
  }

  getRatingStatistics(fromUtc?: string, toUtc?: string): Observable<RatingStatistics> {
    const params: Record<string, string> = {};

    if (fromUtc) {
      params['fromUtc'] = fromUtc;
    }

    if (toUtc) {
      params['toUtc'] = toUtc;
    }

    return this.http.get<RatingStatistics>(`${this.conversationsUrl}/ratings/statistics`, { params });
  }

  sendReaction(conversationId: string, providerMessageId: string, emoji: string): Observable<SendMessageResult> {
    return this.http.post<SendMessageResult>(`${this.conversationsUrl}/${conversationId}/messages/reaction`, {
      providerMessageId,
      emoji,
    });
  }

  sendText(conversationId: string, text: string, repliedToMessageId?: string): Observable<SendMessageResult> {
    return this.http.post<SendMessageResult>(`${this.conversationsUrl}/${conversationId}/messages/text`, {
      text,
      repliedToMessageId: repliedToMessageId ?? null,
    });
  }

  sendTemplate(
    conversationId: string,
    templateName: string,
    languageCode: string,
    parameters: string[],
  ): Observable<SendMessageResult> {
    return this.http.post<SendMessageResult>(`${this.conversationsUrl}/${conversationId}/messages/template`, {
      templateName,
      languageCode,
      parameters,
    });
  }

  sendMedia(conversationId: string, file: File, caption?: string, repliedToMessageId?: string): Observable<SendMessageResult> {
    return this.http.post<SendMessageResult>(
      `${this.conversationsUrl}/${conversationId}/messages/media`,
      this.buildMediaFormData(file, caption, repliedToMessageId),
    );
  }

  /** Conversation Workspace phase: upload-progress variant of sendMedia -- Angular
   * HttpClient tracks the browser->server upload's own byte progress for any POST, which is
   * all the message bubble's progress bar needs (the backend is a single multipart request,
   * not chunked). captureType is Communication Platform v2's Screenshot/Screen Recording
   * addition -- see SendMediaMessageCommand's own remarks on the backend for why it's a
   * separate, optional hint rather than inferred from the file's content type. */
  sendMediaWithProgress(
    conversationId: string,
    file: File,
    caption?: string,
    repliedToMessageId?: string,
    captureType?: 'Screenshot' | 'ScreenRecording',
  ): Observable<HttpEvent<SendMessageResult>> {
    return this.http.post<SendMessageResult>(
      `${this.conversationsUrl}/${conversationId}/messages/media`,
      this.buildMediaFormData(file, caption, repliedToMessageId, captureType),
      { reportProgress: true, observe: 'events' },
    );
  }

  /** Conversation Workspace phase: re-sends an existing message's content into a different,
   * already existing conversation -- see ForwardMessageCommand's own remarks on the backend
   * for why this is WhatsApp-only and existing-conversations-only. */
  forwardMessage(conversationId: string, messageId: string, targetConversationId: string): Observable<SendMessageResult> {
    return this.http.post<SendMessageResult>(`${this.conversationsUrl}/${conversationId}/messages/${messageId}/forward`, {
      targetConversationId,
    });
  }

  private buildMediaFormData(
    file: File,
    caption?: string,
    repliedToMessageId?: string,
    captureType?: 'Screenshot' | 'ScreenRecording',
  ): FormData {
    const formData = new FormData();
    formData.append('file', file);

    if (caption) {
      formData.append('caption', caption);
    }

    if (repliedToMessageId) {
      formData.append('repliedToMessageId', repliedToMessageId);
    }

    if (captureType) {
      formData.append('captureType', captureType);
    }

    return formData;
  }
}
